/**
 * navigation/appShell.tsx
 * The signed-in SK Hire shell. Per the design reference there is NO topbar —
 * page headers live inside each screen. Layout: a docked Sidebar (full 238px on
 * wide, 64px icon-rail on compact) beside the scrolling content that hosts the
 * app navigator. Below the mobile breakpoint the sidebar becomes an overlay
 * drawer, opened from a slim mobile header (the only chrome on small screens).
 */

import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Pressable, TouchableOpacity } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useBreakpoints } from '../theme/responsive';
import { web } from '../utils/web';
import { Icon } from '../components/ui';
import Sidebar from '../shell/sidebar';
import AppStack from './appStack';

interface AppShellProps {
  /** Sign out → root navigator hands back to the platform auth. */
  onSignOut: () => void;
}

export default function AppShell({ onSignOut }: AppShellProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { isMobile, isCompact } = useBreakpoints();

  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { if (!isMobile && drawerOpen) setDrawerOpen(false); }, [isMobile, drawerOpen]);

  return (
    <View style={[styles.app, { backgroundColor: colors.paper }]}>
      {/* Docked sidebar — wide + compact (icon rail). */}
      {!isMobile && <Sidebar onLogout={onSignOut} compact={isCompact} />}

      <View style={styles.main}>
        {/* Slim mobile header (no topbar in the design; this only shows < 820). */}
        {isMobile && (
          <View style={[styles.mobileHeader, { backgroundColor: colors.card, borderBottomColor: colors.line }]}>
            <TouchableOpacity onPress={() => setDrawerOpen(true)} accessibilityLabel="Open menu" style={styles.menuBtn}>
              <Icon name="menu" size={20} color={colors.ink} />
            </TouchableOpacity>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <View style={[styles.logoMark, { backgroundColor: colors.accent, borderRadius: radii.md }]}>
                <Text style={{ color: colors.onAccent, fontWeight: fontWeight.bold, fontSize: 11, letterSpacing: -0.3 }}>SK</Text>
              </View>
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>SK Hire</Text>
            </View>
          </View>
        )}

        <View style={[styles.content, { backgroundColor: colors.paper }]}>
          <AppStack />
        </View>
      </View>

      {/* Mobile overlay drawer. */}
      {isMobile && drawerOpen && (
        <View style={styles.overlay}>
          <Sidebar mobile onLogout={() => { setDrawerOpen(false); onSignOut(); }} onNavigate={() => setDrawerOpen(false)} />
          <Pressable style={styles.scrim} onPress={() => setDrawerOpen(false)} accessibilityLabel="Close menu" />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  app: {
    flex: 1,
    flexDirection: 'row',
    ...(web({ height: '100vh', overflow: 'hidden' }) as object),
  },
  main: { flex: 1, flexDirection: 'column', minWidth: 0, ...(web({ overflow: 'hidden' }) as object) },
  mobileHeader: {
    height: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
  },
  menuBtn: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  logoMark: { width: 26, height: 26, alignItems: 'center', justifyContent: 'center' },
  content: { flex: 1, ...(web({ overflowY: 'auto' }) as object) },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    flexDirection: 'row',
    ...(web({ zIndex: 100 }) as object),
  },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.45)' },
});
